// --- src/services/authService.ts ---
import { supabase } from '@/lib/supabaseClient';
import { cacheService, CacheKeys } from './cacheService';
import { logger } from './logger';
import { retry, dbCircuitBreaker, shouldRetryDatabaseError } from '../utils/retry';

export const authService = {
  // Get current user's role
  async getCurrentUserRole(): Promise<'admin' | 'employee' | null> {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        console.log('getCurrentUserRole: No user found');
        return null;
      }

      const { data, error } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', user.id)
        .eq('is_active', true)
        .single();

      if (error) {
        console.error('getCurrentUserRole error:', error);
        return null;
      }

      console.log('getCurrentUserRole result:', data?.role);
      return data?.role || null;
    } catch (error) {
      console.error('getCurrentUserRole exception:', error);
      return null;
    }
  },

  // Check if current user is admin
  async isCurrentUserAdmin(): Promise<boolean> {
    try {
      const role = await this.getCurrentUserRole();
      const isAdmin = role === 'admin';
      console.log('isCurrentUserAdmin result:', isAdmin);
      return isAdmin;
    } catch (error) {
      console.error('isCurrentUserAdmin error:', error);
      return false;
    }
  },

  // Get user permissions directly from profiles table
  // PRODUCTION-READY: Uses caching, retry, and circuit breaker for reliability
  // CRITICAL: Always returns within 3 seconds maximum
  // IMPORTANT: Returns cached permissions if available, even if timeout occurs
  async getUserPermissions() {
    // Default permissions object
    const defaultPermissions = {
      hasImportModule: false,
      hasLabelModule: false,
      hasEmailModule: false,
      hasProcurementModule: false,
      hasPosModule: false,
      hasBossDashboard: false,
    };

    // Try to get cached permissions first (even before timeout check)
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (user) {
        const cacheKey = CacheKeys.permissions(user.id);
        const cached = cacheService.get<typeof defaultPermissions>(cacheKey);
        if (cached) {
          console.log('[getUserPermissions] Returning cached permissions immediately');
          return cached;
        }
      }
    } catch (err) {
      // Ignore cache check errors, continue with normal flow
    }

    // Track if the promise completed successfully
    let completed = false;

    // Create a timeout promise that RESOLVES with defaults after 10 seconds
    const timeoutPromise = new Promise<typeof defaultPermissions>((resolve) => {
      setTimeout(async () => {
        if (!completed) {
          // Get user and schema info for better error logging
          let userId = 'unknown';
          let schema = 'unknown';
          try {
            const {
              data: { user },
            } = await supabase.auth.getUser();
            userId = user?.id || 'unknown';
            schema = import.meta.env.VITE_SUPABASE_SCHEMA || 'unknown';
          } catch (err) {
            // Ignore errors when getting user info for logging
          }

          logger.warn(
            `TIMEOUT after 10 seconds, returning defaults`,
            {
              userId,
              schema,
              message: 'Permission fetch timed out - using default permissions',
            },
            'getUserPermissions'
          );
        }
        resolve(defaultPermissions);
      }, 10000);
    });

    // Wrap the actual logic in a promise
    const permissionsPromise = (async () => {
      try {
        logger.debug('Starting permission fetch...', null, 'getUserPermissions');
        const overallStartTime = Date.now();

        // Step 1: Get current user (fast, usually cached by Supabase)
        const userStartTime = Date.now();
        const {
          data: { user },
          error: userError,
        } = await supabase.auth.getUser();
        const userDuration = Date.now() - userStartTime;

        if (userError || !user) {
          logger.debug(
            'No user found, returning defaults',
            { error: userError },
            'getUserPermissions'
          );
          return defaultPermissions;
        }

        // Step 2: Check cache first (5 minute TTL) - THIS SHOULD BE INSTANT
        const cacheCheckStartTime = Date.now();
        const cacheKey = CacheKeys.permissions(user.id);
        const cached = cacheService.get<typeof defaultPermissions>(cacheKey);
        const cacheCheckDuration = Date.now() - cacheCheckStartTime;

        if (cached) {
          const overallDuration = Date.now() - overallStartTime;
          logger.performance(
            'getUserPermissions (cache hit)',
            overallDuration,
            'getUserPermissions'
          );
          logger.debug(
            `Cache HIT in ${overallDuration}ms`,
            {
              userDuration,
              cacheDuration: cacheCheckDuration,
              permissions: cached,
            },
            'getUserPermissions'
          );
          return cached;
        }

        logger.debug('Cache MISS, fetching from database...', null, 'getUserPermissions');

        // Step 3: Fetch from database with circuit breaker + retry (but with shorter timeout)
        // JOIN companies table to get company-level module permissions
        const profileData = await dbCircuitBreaker.execute(async () => {
          return retry(
            async () => {
              const { data, error } = await supabase
                .from('profiles')
                .select(
                  'access_labels, access_email, access_import, company_id, companies!inner(has_import_module, has_label_module, has_email_module, has_procurement_module, has_pos_module, has_boss_dashboard)'
                )
                .eq('id', user.id)
                .eq('is_active', true)
                .single();

              if (error) {
                throw error;
              }

              if (!data) {
                throw new Error('No profile data returned');
              }

              return data;
            },
            {
              maxRetries: 2, // Reduced from 3 to 2 for faster failure
              delay: 300, // Reduced from 500 to 300ms
              backoff: 'exponential',
              shouldRetry: shouldRetryDatabaseError,
              onRetry: (attempt, error) => {
                console.warn(`[getUserPermissions] Retry attempt ${attempt}/2:`, error.message);
              },
            }
          );
        });

        // Step 4: Extract and format permissions
        // Company-level permissions come from companies table
        const companyData = Array.isArray(profileData.companies)
          ? profileData.companies[0]
          : profileData.companies;

        const permissions = {
          hasImportModule: profileData.access_import ?? false,
          hasLabelModule: profileData.access_labels ?? false,
          hasEmailModule: profileData.access_email ?? false,
          hasProcurementModule: companyData?.has_procurement_module ?? false,
          hasPosModule: companyData?.has_pos_module ?? false,
          hasBossDashboard: companyData?.has_boss_dashboard === true,
        };

        // Step 5: Cache the result (5 minutes TTL)
        cacheService.set(cacheKey, permissions, 5 * 60 * 1000);

        const duration = Date.now() - overallStartTime;
        logger.performance('getUserPermissions (database fetch)', duration, 'getUserPermissions');
        logger.info(
          'Permissions loaded successfully',
          { permissions, duration },
          'getUserPermissions'
        );
        completed = true;
        return permissions;
      } catch (error: any) {
        logger.error('Exception in getUserPermissions', error, 'getUserPermissions');

        // If circuit breaker is open, log it
        if (dbCircuitBreaker.getState() === 'open') {
          logger.warn(
            'Circuit breaker is OPEN - too many failures',
            { state: 'open' },
            'getUserPermissions'
          );
        }

        completed = true;
        return defaultPermissions;
      }
    })();

    // Race between the actual promise and timeout
    // Both resolve (never reject), so this will always return a value
    try {
      return await Promise.race([permissionsPromise, timeoutPromise]);
    } catch (error) {
      // This should never happen, but just in case
      console.error('[getUserPermissions] Unexpected error:', error);
      return defaultPermissions;
    }
  },

  // Get current user profile with permissions
  async getCurrentUserProfile() {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        return null;
      }

      const { data, error } = await supabase
        .from('profiles')
        .select(
          'id, role, access_labels, access_email, access_import, access_pos, access_boss_dashboard, full_name'
        )
        .eq('id', user.id)
        .eq('is_active', true)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('getCurrentUserProfile error:', error);
      return null;
    }
  },

  // Get all colleagues (users)
  async getColleagues() {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  // Get user by barcode_id (within same company)
  async getUserByBarcode(barcodeId: string): Promise<string | null> {
    try {
      const {
        data: { user },
        error: authError,
      } = await supabase.auth.getUser();

      if (authError || !user) {
        return null;
      }

      // Get current user's company_id
      const { data: currentUserProfile, error: profileError } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .eq('is_active', true)
        .single();

      if (profileError || !currentUserProfile) {
        // Fallback: search without company filter if company_id not available
        const { data, error } = await supabase
          .from('profiles')
          .select('full_name')
          .eq('barcode_id', barcodeId)
          .eq('is_active', true)
          .single();

        if (error) {
          if (error.code === 'PGRST116') {
            return null;
          }
          throw error;
        }

        return data?.full_name || null;
      }

      // Search within same company
      const { data, error } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('barcode_id', barcodeId)
        .eq('company_id', currentUserProfile.company_id)
        .eq('is_active', true)
        .single();

      if (error) {
        // If no user found, return null (not an error)
        if (error.code === 'PGRST116') {
          return null;
        }
        throw error;
      }

      return data?.full_name || null;
    } catch (error) {
      console.error('getUserByBarcode error:', error);
      return null;
    }
  },

  // Create a new colleague using Edge Function (doesn't log out current user)
  async createColleague(data: {
    full_name: string;
    email: string;
    role: 'admin' | 'employee';
    password: string;
    barcode_id?: string;
  }) {
    // Validate input data
    if (!data.full_name || data.full_name.trim() === '') {
      throw new Error('A teljes név megadása kötelező!');
    }
    if (!data.email || data.email.trim() === '') {
      throw new Error('Az email cím megadása kötelező!');
    }
    if (!data.password || data.password.trim() === '') {
      throw new Error('A jelszó megadása kötelező!');
    }
    if (!data.role || (data.role !== 'admin' && data.role !== 'employee')) {
      throw new Error('Érvénytelen szerepkör!');
    }

    // Get current user ID (admin creating the new user)
    const {
      data: { user: currentUser },
      error: userError,
    } = await supabase.auth.getUser();

    if (userError || !currentUser) {
      throw new Error(
        'Failed to get current user: ' + (userError?.message || 'User not authenticated')
      );
    }

    if (!currentUser.id || currentUser.id.trim() === '') {
      throw new Error('A felhasználó ID nem érhető el!');
    }

    // Get current schema from environment or default to leltarmester_dev
    const currentSchema = import.meta.env.VITE_SUPABASE_SCHEMA || 'leltarmester_dev';

    if (!currentSchema || currentSchema.trim() === '') {
      throw new Error('A séma nem érhető el!');
    }

    // Handle email format (add domain if missing)
    const SYSTEM_DOMAIN = 'example.com';
    const emailToUse = data.email.includes('@') ? data.email : `${data.email}@${SYSTEM_DOMAIN}`;

    // Trim all string values and normalize schema to lowercase
    const fullName = data.full_name.trim();
    const email = emailToUse.trim();
    const password = data.password.trim();
    const role = data.role.trim() as 'admin' | 'employee';
    const schema = currentSchema.trim().toLowerCase(); // Normalize to lowercase
    const adminUserId = currentUser.id.trim();

    // Prepare payload for Edge Function
    // - adminUserId: ID of the current logged-in admin (creating the new user)
    // - schema: From VITE_SUPABASE_SCHEMA environment variable
    // - barcodeId: Optional barcode identifier for the new user
    const payload = {
      email: email,
      password: password,
      fullName: fullName,
      role: role,
      schema: schema, // From import.meta.env.VITE_SUPABASE_SCHEMA
      adminUserId: adminUserId, // Current admin's ID from supabase.auth.getUser()
      barcodeId: data.barcode_id?.trim() || null,
    };

    // Validate payload before sending
    const missingFields: string[] = [];
    if (!payload.email) missingFields.push('email');
    if (!payload.password) missingFields.push('password');
    if (!payload.fullName) missingFields.push('fullName');
    if (!payload.role) missingFields.push('role');
    if (!payload.schema) missingFields.push('schema');
    if (!payload.adminUserId) missingFields.push('adminUserId');

    if (missingFields.length > 0) {
      throw new Error(`Hiányzó kötelező mezők: ${missingFields.join(', ')}`);
    }

    console.log('🟡 [authService] Calling Edge Function with payload:', {
      email: payload.email,
      fullName: payload.fullName,
      role: payload.role,
      schema: payload.schema,
      adminUserId: payload.adminUserId,
      password: '***' + payload.password.length + ' chars***',
    });
    console.log('🟡 [authService] Payload validation:', {
      hasEmail: !!payload.email,
      hasPassword: !!payload.password,
      hasFullName: !!payload.fullName,
      hasRole: !!payload.role,
      hasSchema: !!payload.schema,
      hasAdminUserId: !!payload.adminUserId,
      emailLength: payload.email.length,
      passwordLength: payload.password.length,
      fullNameLength: payload.fullName.length,
      schemaLength: payload.schema.length,
      adminUserIdLength: payload.adminUserId.length,
    });

    // Call the Edge Function
    console.log('🟡 [authService] Invoking Edge Function with:', {
      functionName: 'create-user',
      payloadKeys: Object.keys(payload),
      payloadValues: {
        email: payload.email,
        fullName: payload.fullName,
        role: payload.role,
        schema: payload.schema,
        adminUserId: payload.adminUserId,
        passwordLength: payload.password.length,
      },
    });

    // Use direct fetch (invoke() has issues with body transmission)
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    if (!supabaseUrl || !supabaseAnonKey) {
      throw new Error('Hiányzó Supabase hitelesítési adatok. Ellenőrizd a környezeti változókat.');
    }

    const functionUrl = `${supabaseUrl}/functions/v1/create-user`;
    console.log('🟡 [authService] Calling Edge Function via direct fetch:', functionUrl);
    console.log('🟡 [authService] Payload:', {
      email: payload.email,
      fullName: payload.fullName,
      role: payload.role,
      schema: payload.schema,
      adminUserId: payload.adminUserId,
      passwordLength: payload.password.length,
    });

    // Final payload verification log (password redacted for security)
    console.log('🚀 PAYLOAD:', {
      ...payload,
      password: '***REDACTED***',
    });

    // Call Edge Function with proper authentication headers
    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${supabaseAnonKey}`, // Bearer token for auth
        apikey: supabaseAnonKey, // API key required by Supabase Edge Functions
      },
      body: JSON.stringify(payload),
    });

    console.log('🟡 [authService] Response status:', response.status);
    console.log('🟡 [authService] Response ok:', response.ok);

    let result: any = null;
    let error: any = null;

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ [authService] Error response body:', errorText);
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { error: errorText || `HTTP ${response.status}` };
      }
      error = new Error(errorData.error || `HTTP ${response.status}: ${errorText}`);
    } else {
      try {
        result = await response.json();
        console.log('✅ [authService] Success response:', {
          hasUser: !!result.user,
          hasProfile: !!result.profile,
          message: result.message,
        });
      } catch (parseError: any) {
        console.error('❌ [authService] Failed to parse success response:', parseError);
        error = new Error('A szerver válasza nem értelmezhető.');
      }
    }

    console.log('🟡 [authService] Edge Function response:', {
      hasData: !!result,
      hasError: !!error,
      resultKeys: result ? Object.keys(result) : [],
      errorMessage: error?.message,
    });

    // Handle errors - extract JSON body if available
    if (error) {
      console.error('❌ [authService] Edge Function invoke error:', error);

      // Try to extract the actual error message from the response
      let errorMessage = error.message || 'Unknown error';

      // If error has context or response, try to parse it
      if (error.context) {
        console.error('❌ [authService] Error context:', error.context);

        // Try to extract error from context if it's a Response object
        if (error.context instanceof Response) {
          try {
            const errorBody = await error.context.clone().json();
            console.error('❌ [authService] Error response body from context:', errorBody);
            if (errorBody.error) {
              errorMessage = errorBody.error;
            }
          } catch (e) {
            console.warn('⚠️ [authService] Could not parse error response body from context');
            // Try to get text instead
            try {
              const errorText = await error.context.clone().text();
              console.error('❌ [authService] Error response text:', errorText);
              if (errorText) {
                errorMessage = errorText;
              }
            } catch (textError) {
              console.warn('⚠️ [authService] Could not read error response text');
            }
          }
        }
      }

      // Check if there's a response body with JSON error
      if (error.response) {
        try {
          const errorBody = await error.response.json();
          console.error('❌ [authService] Error response body:', errorBody);
          if (errorBody.error) {
            errorMessage = errorBody.error;
          }
        } catch (e) {
          console.warn('⚠️ [authService] Could not parse error response body');
          // Try to get text instead
          try {
            const errorText = await error.response.text();
            console.error('❌ [authService] Error response text:', errorText);
            if (errorText) {
              errorMessage = errorText;
            }
          } catch (textError) {
            console.warn('⚠️ [authService] Could not read error response text');
          }
        }
      }

      // Provide more helpful error message
      const friendlyMessage = errorMessage.includes('Missing required fields')
        ? 'Hiányzó kötelező mezők. Ellenőrizd, hogy minden adat meg van-e adva.'
        : errorMessage.includes('Invalid schema format')
          ? 'Érvénytelen séma formátum. Kérlek, lépj kapcsolatba a támogatással.'
          : errorMessage.includes('Password must be at least')
            ? 'A jelszó legalább 6 karakter hosszú kell legyen.'
            : errorMessage.includes('Auth Error')
              ? `Felhasználó létrehozási hiba: ${errorMessage.replace('Auth Error: ', '')}`
              : errorMessage.includes('DB Error')
                ? `Adatbázis hiba: ${errorMessage.replace('DB Error: ', '')}`
                : errorMessage;

      throw new Error(friendlyMessage);
    }

    if (result?.error) {
      console.error('❌ [authService] Edge Function returned error in result:', result.error);
      throw new Error(result.error);
    }

    if (!result?.user) {
      console.error('❌ [authService] No user data in result:', result);
      throw new Error('User creation succeeded but no user data returned');
    }

    console.log('✅ [authService] User created, ID:', result.user.id);

    // Fetch the profile from the database to return it
    // The Edge Function creates the profile, but we need to fetch it to get the full profile object
    const { data: profileData, error: profileError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', result.user.id)
      .single();

    if (profileError) {
      // Profile might not be immediately available, but user was created successfully
      console.warn('Profile fetch error (user was created):', profileError);
      // Return a minimal profile object based on the user data
      return {
        id: result.user.id,
        email: emailToUse,
        full_name: data.full_name,
        role: data.role,
        is_active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
    }

    // Return the profile data
    return profileData;
  },

  // Update colleague status (active/inactive)
  async updateColleagueStatus(id: string, isActive: boolean) {
    const { data, error } = await supabase
      .from('profiles')
      .update({ is_active: isActive })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    // Invalidate cache for this user's permissions
    cacheService.invalidate(CacheKeys.permissions(id));
    console.log(`[updateColleagueStatus] Cache invalidated for user: ${id}`);

    return data;
  },

  // Update user permissions (access_labels, access_email, access_import)
  async updateUserPermissions(
    userId: string,
    permissions: {
      access_labels?: boolean;
      access_email?: boolean;
      access_import?: boolean;
      access_pos?: boolean;
    }
  ) {
    const { data, error } = await supabase
      .from('profiles')
      .update(permissions)
      .eq('id', userId)
      .select()
      .single();

    if (error) throw error;

    // CRITICAL: Invalidate cache when permissions change
    cacheService.invalidate(CacheKeys.permissions(userId));
    console.log(`[updateUserPermissions] Cache invalidated for user: ${userId}`);

    return data;
  },

  // Delete colleague
  async deleteColleague(id: string) {
    const { error } = await supabase.from('profiles').delete().eq('id', id);

    if (error) throw error;
  },

  // Update user role
  async updateUserRole(userId: string, newRole: 'admin' | 'employee') {
    const { data, error } = await supabase
      .from('profiles')
      .update({ role: newRole })
      .eq('id', userId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },
};
